import { useEffect, useState } from 'react';

/**
 * useOnlineStatus — tracks connectivity for the offline banner.
 *
 * `navigator.onLine` is a weak signal: it reports true whenever the device has
 * *any* network interface up, which in a workshop means "connected to the wifi
 * AP that has no uplink". It is still the only synchronous signal available, so
 * it is used as the optimistic baseline and corrected by observed failures:
 * axiosInstance reports real network errors through reportNetworkFailure(), and
 * a successful response clears the flag through reportNetworkSuccess().
 *
 * The result is a banner that appears when requests actually stop working, not
 * only when the OS notices the radio dropped.
 */

const listeners = new Set();

// null = no observation yet, defer to navigator.onLine.
let observedReachable = null;

const emit = () => { listeners.forEach((fn) => fn()); };

const computeOnline = () => {
    const navigatorOnline = typeof navigator === 'undefined' ? true : navigator.onLine !== false;
    if (!navigatorOnline) return false;
    return observedReachable !== false;
};

/** Called by the axios error interceptor when a request never reached the server. */
export const reportNetworkFailure = () => {
    if (observedReachable === false) return;
    observedReachable = false;
    emit();
};

/** Called by the axios response interceptor on any completed round trip. */
export const reportNetworkSuccess = () => {
    if (observedReachable === true) return;
    observedReachable = true;
    emit();
};

const useOnlineStatus = () => {
    const [online, setOnline] = useState(computeOnline);

    useEffect(() => {
        const sync = () => setOnline(computeOnline());

        // The OS says the radio is back. Clear the observed-failure flag so the
        // banner lifts immediately rather than waiting for the next successful
        // request — otherwise an idle app stays "offline" forever.
        const handleOnline  = () => { observedReachable = null; sync(); };
        const handleOffline = sync;

        listeners.add(sync);
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        // Correct any state that changed between the initial render and effect.
        sync();

        return () => {
            listeners.delete(sync);
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    return online;
};

export default useOnlineStatus;
